import React, { useState } from 'react';
import { LanguageCode } from '../types';
import { translations } from '../i18n/translations';
import { ShieldCheck, UserPlus, CreditCard, Sparkles, Check, ArrowRight, Lock, Loader2 } from 'lucide-react';
import { submitMembershipApplicationApi } from '../services/api';

interface MembershipViewProps {
  currentLang: LanguageCode;
  onOpenAuthModal: () => void;
  onNavClick: (tab: string) => void;
}

export const MembershipView: React.FC<MembershipViewProps> = ({
  currentLang,
  onOpenAuthModal,
  onNavClick,
}) => {
  const t = translations[currentLang];

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    city: '',
    address: '',
    notes: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await submitMembershipApplicationApi({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        address: formData.address,
        notes: formData.notes,
      });
      setFormSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Üyelik başvurusu kaydedilirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto space-y-8 py-4">
      {/* Header Banner */}
      <div className="bg-[#132238] rounded-3xl p-6 sm:p-10 border-2 border-[#38BDF8]/40 shadow-[0_0_35px_rgba(56,189,248,0.2)] relative overflow-hidden text-center sm:text-left flex flex-col sm:flex-row items-center justify-between gap-6">
        <div className="space-y-3 max-w-xl">
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-[#0B1426] border border-[#38BDF8]/40 text-[#38BDF8] text-xs font-extrabold uppercase tracking-wider shadow-md">
            <Sparkles className="w-3.5 h-3.5" /> Resmi Dernek Üyeliği
          </span>
          <h1 className="text-2xl sm:text-4xl font-extrabold text-white font-poppins">
            Topluluğumuza Katılın, Geleceğe Güç Verin
          </h1>
          <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">
            Komeliya Çandî ya Fransî-Kurdî ya Yvelines resmi dernek üyesi olarak kültürel etkinliklere öncelikli katılım, dijital kimlik kartı ve üye ayrıcalıklarından yararlanın.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 shrink-0">
          <button
            onClick={onOpenAuthModal}
            className="px-6 py-3 rounded-2xl bg-[#38BDF8] text-[#0B1426] font-extrabold text-xs hover:bg-[#A5F3FC] hover:shadow-[0_0_20px_rgba(165,243,252,0.8)] transition-all flex items-center justify-center gap-2 shadow-lg"
          >
            <Lock className="w-4 h-4" /> Üye Girişi Yap
          </button>
        </div>
      </div>

      {/* Digital ID Card Preview & Benefits Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Sample Digital Membership Card */}
        <div className="bg-[#132238] rounded-3xl p-6 sm:p-8 border-2 border-[#38BDF8] shadow-[0_0_30px_rgba(56,189,248,0.3)] relative overflow-hidden flex flex-col justify-between">
          <div className="absolute top-0 right-0 translate-x-8 -translate-y-8 w-40 h-40 bg-[#38BDF8]/10 rounded-full blur-2xl pointer-events-none" />

          <div className="flex items-center justify-between pb-6 border-b border-[#38BDF8]/20">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#0B1426] border-2 border-[#38BDF8] flex items-center justify-center text-xl text-white font-bold shadow-[0_0_15px_rgba(56,189,248,0.4)]">
                ☀️
              </div>
              <div>
                <span className="text-[10px] font-extrabold text-[#38BDF8] uppercase tracking-widest block">KOMELIYA ÇANDÎ YA YVELINES</span>
                <span className="text-sm font-extrabold text-white font-poppins block">DİJİTAL ÜYE KARTI</span>
              </div>
            </div>
            <span className="px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[10px] font-extrabold uppercase">
              AKTİF
            </span>
          </div>

          <div className="py-6 space-y-2">
            <p className="text-xs text-cyan-200/80 font-bold">Örnek Üye Profili:</p>
            <h3 className="text-xl font-extrabold text-white font-poppins">Rêber Yılmaz</h3>
            <p className="text-xs text-gray-300">Sicil No: <strong className="text-[#38BDF8]">KDK-2026-0842</strong></p>
            <p className="text-xs text-gray-300">Üyelik Tipi: <strong>Asil Üye</strong></p>
          </div>

          <div className="pt-4 border-t border-[#38BDF8]/20 flex items-center justify-between text-xs text-gray-400">
            <span>Fransa Yvelines Bölgesi</span>
            <span className="text-[10px] bg-[#0B1426] border border-[#38BDF8]/30 text-[#38BDF8] px-2 py-0.5 rounded font-mono font-bold">2026 VALID</span>
          </div>
        </div>

        {/* Membership Benefits List */}
        <div className="bg-[#132238] rounded-3xl p-6 sm:p-8 border-2 border-[#38BDF8]/30 flex flex-col justify-between shadow-xl">
          <div>
            <h3 className="text-lg font-extrabold text-[#38BDF8] font-poppins mb-4 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-400" /> Üyelik Avantajları
            </h3>
            <ul className="space-y-3 text-xs text-gray-200">
              <li className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-[#0B1426] border border-[#38BDF8] text-[#38BDF8] flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                  <Check className="w-3 h-3" />
                </span>
                <span>Tüm kültür, sanat, Newroz ve festival organizasyonlarına indirimli ve öncelikli bilet imkanı.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-[#0B1426] border border-[#38BDF8] text-[#38BDF8] flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                  <Check className="w-3 h-3" />
                </span>
                <span>Kürtçe dil kursları, govend (halk oyunları) ve enstrüman atölyelerine ücretsiz kayıt.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-[#0B1426] border border-[#38BDF8] text-[#38BDF8] flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                  <Check className="w-3 h-3" />
                </span>
                <span>Genel kurul toplantılarında seçme ve seçilme oy hakkı.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-[#0B1426] border border-[#38BDF8] text-[#38BDF8] flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                  <Check className="w-3 h-3" />
                </span>
                <span>Online üye paneli üzerinden aidat ve dijital üyelik kartı takibi.</span>
              </li>
            </ul>
          </div>

          <div className="mt-6 pt-4 border-t border-[#38BDF8]/20 flex items-center justify-between">
            <span className="text-xs text-gray-300">Zaten hesabınız var mı?</span>
            <button
              onClick={onOpenAuthModal}
              className="text-xs font-extrabold text-[#38BDF8] hover:underline flex items-center gap-1"
            >
              Giriş Yapın <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Online Membership Application Form */}
      <div className="bg-[#132238] rounded-3xl p-6 sm:p-10 border-2 border-[#38BDF8]/30 shadow-xl">
        <h3 className="text-xl font-extrabold text-white font-poppins mb-2">
          📝 Online Üyelik Ön Başvuru Formu
        </h3>
        <p className="text-xs text-gray-300 mb-6">
          Aşağıdaki formu doldurarak dernek yönetim kurulumuza online üyelik başvurusu iletebilirsiniz.
        </p>

        {formSubmitted ? (
          <div className="bg-emerald-950/80 border-2 border-emerald-500/60 p-6 rounded-2xl text-center space-y-3">
            <span className="text-3xl block">🎉</span>
            <h4 className="text-lg font-bold text-white">Üyelik Başvurunuz Veritabanına Kaydedildi!</h4>
            <p className="text-xs text-emerald-200 max-w-lg mx-auto">
              Başvurunuz veritabanımızda güvenle saklandı. Yönetim kurulumuz tarafından incelendikten sonra tarafınıza e-posta ve SMS ile onay bilgisi iletilecektir.
            </p>
            <button
              onClick={() => onNavClick('member')}
              className="px-6 py-2.5 rounded-xl bg-[#38BDF8] text-[#0B1426] font-extrabold text-xs mt-2 hover:bg-[#A5F3FC]"
            >
              Üye Paneline Git
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {error && (
              <div className="col-span-full p-3 rounded-xl bg-red-950/80 border border-red-500/50 text-red-200 text-xs">
                {error}
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-300 mb-1">Ad Soyad *</label>
              <input
                type="text"
                required
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                placeholder="Örn: Azad Yılmaz"
                className="w-full bg-[#0B1426] border border-[#38BDF8]/30 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#38BDF8]"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-300 mb-1">E-Posta Adresi *</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="ornek@email.com"
                className="w-full bg-[#0B1426] border border-[#38BDF8]/30 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#38BDF8]"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-300 mb-1">Telefon Numarası *</label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+33 6 00 00 00 00"
                className="w-full bg-[#0B1426] border border-[#38BDF8]/30 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#38BDF8]"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-300 mb-1">Şehir</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="Örn: Mantes-la-Jolie"
                className="w-full bg-[#0B1426] border border-[#38BDF8]/30 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#38BDF8]"
              />
            </div>

            <div className="col-span-full">
              <label className="block text-xs font-semibold text-gray-[#38BDF8] mb-1">Adres</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Açık Adres"
                className="w-full bg-[#0B1426] border border-[#38BDF8]/30 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#38BDF8]"
              />
            </div>

            <div className="col-span-full">
              <label className="block text-xs font-semibold text-gray-300 mb-1">Ek Notlar / Açıklama</label>
              <textarea
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Varsa belirtmek istediğiniz konular..."
                className="w-full bg-[#0B1426] border border-[#38BDF8]/30 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#38BDF8]"
              />
            </div>

            <div className="col-span-full flex justify-end pt-2">
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-3.5 rounded-2xl bg-[#38BDF8] text-[#0B1426] font-extrabold text-xs hover:bg-[#A5F3FC] hover:shadow-[0_0_20px_rgba(165,243,252,0.8)] transition-all flex items-center gap-2 shadow-lg disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Kaydediliyor...</span>
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4" />
                    <span>Üyelik Başvurusunu Gönder</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
